/**
 * One column, as a card: its name and type side by side, its constraints as
 * switches, and its default — the answers a column definition needs, and the
 * SQL they make. Shared by "New table" and "Add column", so a column is
 * described the same way whether the table exists yet or not.
 */
import ToggleSwitchCheckbox from '../../components/FormInputField/ToggleSwitchCheckbox/ToggleSwitchCheckbox';
import { DsButton, Eyebrow, SurfaceCard } from '../../components/ds';
import { quoteIdent } from './cells';
import { Field, inputClass } from './SideDrawer';

export interface ColumnSpec {
  /** Stable key for the list of a new table's columns; never written to SQL. */
  key: string;
  name: string;
  type: string;
  customType: string;
  isRequired: boolean;
  isArray: boolean;
  isUnique: boolean;
  defaultValue: string;
}

/** The types almost every app column is, each with the one fact that decides it. */
export const COLUMN_TYPES: { value: string, sql: string, label: string, hint: string }[] = [
  { value: 'text', sql: 'text', label: 'Text · text', hint: 'Any length.' },
  { value: 'varchar', sql: 'varchar(255)', label: 'Short text · varchar(255)', hint: 'Capped at 255 characters.' },
  { value: 'int8', sql: 'bigint', label: 'Integer · int8', hint: 'Whole numbers.' },
  { value: 'numeric', sql: 'numeric(10,2)', label: 'Decimal · numeric(10,2)', hint: 'Exact — use for money.' },
  { value: 'bool', sql: 'boolean', label: 'Boolean · bool', hint: 'true or false.' },
  { value: 'timestamptz', sql: 'timestamptz', label: 'Date and time · timestamptz', hint: 'Stored in UTC.' },
  { value: 'date', sql: 'date', label: 'Date · date', hint: 'No time part.' },
  { value: 'jsonb', sql: 'jsonb', label: 'JSON · jsonb', hint: 'Queryable, not orderable.' },
  { value: 'uuid', sql: 'uuid', label: 'UUID · uuid', hint: 'gen_random_uuid() makes one.' },
];

const CUSTOM = '__custom__';

let nextKey = 0;

export const newColumnSpec = (patch: Partial<ColumnSpec> = {}): ColumnSpec => ({
  key: `column-${(nextKey += 1)}`,
  name: '',
  type: 'text',
  customType: '',
  isRequired: false,
  isArray: false,
  isUnique: false,
  defaultValue: '',
  ...patch,
});

export const resolvedType = (spec: ColumnSpec) => {
  const base = spec.type === CUSTOM
    ? spec.customType.trim()
    : COLUMN_TYPES.find(item => item.value === spec.type)?.sql || spec.type;

  return base && spec.isArray && !base.endsWith('[]') ? `${base}[]` : base;
};

/** `status text NOT NULL DEFAULT 'draft'` — or empty while it is incomplete. */
export const columnDefinition = (spec: ColumnSpec) => {
  const type = resolvedType(spec);

  if (!spec.name.trim() || !type) {
    return '';
  }

  return [
    quoteIdent(spec.name.trim()),
    type,
    spec.isRequired ? 'NOT NULL' : '',
    // Unique on an array compares whole lists, which is never what is meant;
    // the switch is locked off for arrays and never reaches the SQL.
    spec.isUnique && !spec.isArray ? 'UNIQUE' : '',
    // Written as an expression, not quoted for them: `now()`, `0` and
    // `'draft'` are all defaults, and only the person typing knows which.
    spec.defaultValue.trim() ? `DEFAULT ${spec.defaultValue.trim()}` : '',
  ].filter(Boolean).join(' ');
};

export const NAME_HINT = 'Lowercase letters, numbers and _ — anything else needs quotes in SQL.';

const ColumnCard = ({ label, spec, onChange, onRemove, autoFocus }: {
  /** `column 2`, or `new column on customers`. */
  label: string,
  spec: ColumnSpec,
  onChange: (patch: Partial<ColumnSpec>) => void,
  /** Absent when the card cannot go — the last column of a new table. */
  onRemove?: () => void,
  autoFocus?: boolean,
}) => {
  const typeHint = spec.type === CUSTOM
    ? 'Any type the database knows: inet, citext, interval…'
    : COLUMN_TYPES.find(item => item.value === spec.type)?.hint;

  return (
    <SurfaceCard tone="surface" className="px-[18px] py-4">
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-3 min-h-[30px]">
          <Eyebrow>{label}</Eyebrow>
          {onRemove ? (
            <DsButton variant="ghost" size="md" onClick={onRemove}>Remove</DsButton>
          ) : null}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
          <Field label="Name" isRequired>
            <input
              autoFocus={autoFocus}
              value={spec.name}
              onChange={event => onChange({ name: event.target.value })}
              placeholder="status"
              spellCheck={false}
              className={inputClass}
            />
          </Field>

          <Field label="Type" isRequired hint={typeHint}>
            <select
              value={spec.type}
              onChange={event => onChange({ type: event.target.value })}
              className={`${inputClass} font-courrier`}
            >
              {COLUMN_TYPES.map(item => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
              <option value={CUSTOM}>Other type…</option>
            </select>
          </Field>

          {spec.type === CUSTOM ? (
            <div className="sm:col-start-2">
              <Field label="Type name" isRequired>
                <input
                  value={spec.customType}
                  onChange={event => onChange({ customType: event.target.value })}
                  placeholder="inet"
                  spellCheck={false}
                  className={`${inputClass} font-courrier`}
                />
              </Field>
            </div>
          ) : null}
        </div>

        <div className="flex flex-col gap-3">
          <Eyebrow>constraints</Eyebrow>
          <ToggleSwitchCheckbox
            labelText="Required"
            description="A row cannot be inserted without this column."
            value={spec.isRequired}
            onChange={event => onChange({ isRequired: event.target.checked })}
          />
          <ToggleSwitchCheckbox
            labelText="Array"
            description="Stores a list of values instead of one."
            value={spec.isArray}
            // An array cannot be unique, so turning Array on turns Unique off.
            onChange={event => onChange(event.target.checked
              ? { isArray: true, isUnique: false }
              : { isArray: false })}
          />
          <ToggleSwitchCheckbox
            labelText="Unique"
            description="No two rows can share this value. Adds an index."
            value={spec.isUnique}
            disabled={spec.isArray}
            infoText={spec.isArray ? 'Not available on array columns.' : null}
            onChange={event => onChange({ isUnique: event.target.checked })}
          />
        </div>

        <Field
          label="Default value"
          hint={spec.isRequired ? 'Applied when the column is left out of an insert.' : 'Left out means NULL.'}
        >
          <input
            value={spec.defaultValue}
            onChange={event => onChange({ defaultValue: event.target.value })}
            placeholder="now(), 0, 'draft'"
            spellCheck={false}
            className={`${inputClass} font-courrier`}
          />
        </Field>
      </div>
    </SurfaceCard>
  );
};

export default ColumnCard;
